/** Why precipitation couldn't be loaded: the precipitation manifest and day files, or a point's
 *  hourly range. Both are read from the precipitation files a scheduled job builds, so `unavailable`
 *  (503) means nothing has been built yet or its index is unreadable, and the gateway statuses (504,
 *  502) mean the API itself didn't answer. Every reason is transient, and the weather surfaces are
 *  optional, which is why these are explained quietly beside them (the Scheduling map retries while
 *  weather is shown), never raised as an app-wide error. */
export type WeatherUnavailableReason = "unavailable" | "timed-out" | "gateway-error" | "unexpected";

const EXPLANATIONS: Record<WeatherUnavailableReason, string> = {
	unavailable: "Precipitation data isn't available right now.",
	"timed-out": "The weather service timed out.",
	"gateway-error": "The weather service returned an error.",
	unexpected: "Precipitation couldn't be loaded.",
};

/** The one-sentence, user-facing account of a {@link WeatherUnavailableReason}. */
export function describeWeatherUnavailable(reason: WeatherUnavailableReason): string {
	return EXPLANATIONS[reason];
}

/** A failed manifest, day-file or range fetch, named by why it failed. Its `message` is the
 *  user-facing explanation. */
export class WeatherUnavailableError extends Error {
	constructor(
		readonly reason: WeatherUnavailableReason,
		options?: ErrorOptions,
	) {
		super(describeWeatherUnavailable(reason), options);
		this.name = "WeatherUnavailableError";
	}
}
